import React, { useRef, useState, useEffect, useCallback } from "react";
import { Box, Typography, Container, Stack, IconButton, useTheme, useMediaQuery } from "@mui/material";
import { motion } from "framer-motion";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { fadeInUp } from "../utils/animations";

const quoteMap = {
  'Dr. Arjun Dang-Dr. Dangs': "NirogGyan's smart reports are phenomenal! They provide clear information and easy to understand insights to the patients and us. The team is committed towards excellence, working closely with us to deliver beautiful white-label Dx products.",
  'Dr. Danish-Paediatrician in Oman & CEO of Zaincare': "I'm extremely happy with the collaboration extended by the team. The patient-friendly offerings have become a highlight of our sales & marketing efforts in Dubai, as we look to spread across the Middle East with satisfied customers.",
  'Ashwani-PharmEasy': "Smart Report is our special advantage at PharmEasy-Thyrocare, to boost user-engagement for a variety of customers. The wide test & use-case coverage makes it a very effective tool with a great ROI.",
};

// Parse quoteMap and create testimonials array
const testimonials = Object.entries(quoteMap).map(([key, text], index) => {
  const [name, ...roleParts] = key.split('-');
  const role = roleParts.join('-'); // Join back in case role contains hyphens

  // Map to first 3 images
  const images = [
    "https://niroggyan.s3.ap-south-1.amazonaws.com/brochure-images/client-image-dynamicSRpic.png",
    "https://niroggyan.s3.ap-south-1.amazonaws.com/brochure-images/client-image-advanceSRpic.jpg",
    "https://niroggyan.s3.ap-south-1.amazonaws.com/brochure-images/client-image-compactSRpic.png",
  ];

  return {
    image: images[index],
    name: name.trim(),
    role: role.trim(),
    text: text,
    rating: 5,
  };
});

const TestimonialsSection = () => {
  const scrollContainerRef = useRef(null);
  const cardRefs = useRef([]);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [maxScroll, setMaxScroll] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  // Update max scroll and position on resize and scroll
  useEffect(() => {
    const updateScrollInfo = () => {
      if (scrollContainerRef.current) {
        const container = scrollContainerRef.current;
        setMaxScroll(container.scrollWidth - container.clientWidth);
        setScrollPosition(container.scrollLeft);
      }
    };

    updateScrollInfo();
    window.addEventListener('resize', updateScrollInfo);
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', updateScrollInfo);
    }

    return () => {
      window.removeEventListener('resize', updateScrollInfo);
      if (container) {
        container.removeEventListener('scroll', updateScrollInfo);
      }
    };
  }, [testimonials.length]);

  // Calculate scroll amount dynamically based on card width + gap
  const calculateScrollAmount = useCallback(() => {
    if (cardRefs.current.length === 0 || !cardRefs.current[0]) return 400;

    const firstCard = cardRefs.current[0];
    const cardRect = firstCard.getBoundingClientRect();
    const cardWidth = cardRect.width;

    // Get gap from computed styles (gap: { xs: 2.5, sm: 3 } = 20px, 24px)
    const gap = isMobile ? 20 : 24; // Approximate gap in pixels

    return cardWidth + gap;
  }, [isMobile]);

  const scroll = useCallback(
    (direction) => {
      if (!scrollContainerRef.current) return;

      const container = scrollContainerRef.current;
      const scrollAmount = calculateScrollAmount();
      const currentScroll = container.scrollLeft;
      const containerWidth = container.clientWidth;
      const scrollWidth = container.scrollWidth;

      const isAtEnd = currentScroll + containerWidth >= scrollWidth - 1;
      const isAtStart = currentScroll <= 1;

      // RIGHT
      if (direction === "right" && isAtEnd) {
        container.scrollTo({ left: 0, behavior: "smooth" });
        return;
      }

      if (direction === "right") {
        container.scrollBy({ left: scrollAmount, behavior: "smooth" });
        return;
      }

      // LEFT
      if (direction === "left" && isAtStart) {
        container.scrollTo({ left: scrollWidth, behavior: "smooth" });
        return;
      }

      if (direction === "left") {
        container.scrollBy({ left: -scrollAmount, behavior: "smooth" });
      }
    },
    [calculateScrollAmount]
  );


  const TestimonialCard = ({ testimonial, index }) => (
    <Box
      ref={(el) => {
        if (el) cardRefs.current[index] = el;
      }}
      sx={{
        minWidth: { xs: "320px", sm: "380px", md: "400px" },
        maxWidth: { xs: "320px", sm: "380px", md: "400px" },
        flexShrink: 0,
        p: { xs: 3, sm: 3.5, md: 4 },
        borderRadius: "20px",
        background: "#ffffff",
        boxShadow: "0 8px 24px rgba(53, 103, 214, 0.12)",
        border: "1px solid rgba(53, 103, 214, 0.1)",
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 32px rgba(53, 103, 214, 0.18)",
          borderColor: "rgba(53, 103, 214, 0.2)",
        },
      }}
    >
      <Stack spacing={2.5} alignItems="center" textAlign="center">
        {/* Profile Image */}
        <Box
          component="img"
          src={testimonial.image}
          alt={testimonial.name}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = `https://placehold.co/80x80/3567D6/ffffff?text=${encodeURIComponent(testimonial.name.charAt(0))}`;
          }}
          sx={{
            width: { xs: "70px", sm: "80px" },
            height: { xs: "70px", sm: "80px" },
            borderRadius: "50%",
            objectFit: "cover",
            border: "3px solid #ffffff",
            boxShadow: "0 4px 16px rgba(53, 103, 214, 0.2)",
          }}
        />

        {/* Rating Stars */}
        <Box sx={{ display: "flex", gap: 0.5 }}>
          {[...Array(testimonial.rating)].map((_, i) => (
            <Star key={i} size={18} fill="#fbbf24" color="#fbbf24" />
          ))}
        </Box>

        {/* Testimonial Text */}
        <Typography
          sx={{
            color: "#475569",
            fontSize: { xs: "13px", sm: "14px" },
            lineHeight: 1.8,
            fontFamily: "'Poppins', sans-serif",
            fontStyle: "italic",
            minHeight: { xs: "90px", sm: "100px" },
          }}
        >
          {testimonial.text}
        </Typography>

        {/* Name and Role */}
        <Box>
          <Typography
            sx={{
              fontWeight: 600,
              color: "#1D47A4",
              fontSize: { xs: "15px", sm: "16px" },
              mb: 0.5,
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            {testimonial.name}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "12px", sm: "13px" },
              color: "#64748b",
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            {testimonial.role}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: "#ffffff",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 }, position: "relative", zIndex: 1 }}>
        {/* Section Header */}
        <Box
          component={motion.div}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeInUp}
          sx={{ textAlign: "center", mb: 6 }}
        >
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "36px", md: "48px" },
              fontWeight: 700,
              color: "#1D47A4",
              mb: 2,
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            Testimonials
          </Typography>
          <Typography
            sx={{
              color: "#7F7F7F",
              maxWidth: "650px",
              mx: "auto",
              fontSize: { xs: "14px", md: "16px" },
              lineHeight: 1.7,
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            These cases are perfectly simple and easy to distinguish. In a free
            hour, when our power of choice is untrammelled and when nothing
            prevents our being able to do.
          </Typography>
        </Box>
      </Container>

      {/* Manual Scroll Testimonials with Arrow Buttons */}
      <Box
        sx={{
          mt: 4,
          position: "relative",
        }}
      >
        {/* Left Arrow Button */}
        <IconButton
          onClick={() => scroll("left")}
          sx={{
            position: "absolute",
            left: { xs: 8, sm: 16, md: 24 },
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 2,
            backgroundColor: "#ffffff",
            color: "#3567D6",
            width: { xs: "40px", sm: "48px" },
            height: { xs: "40px", sm: "48px" },
            boxShadow: "0 4px 16px rgba(53, 103, 214, 0.2)",
            border: "1px solid rgba(53, 103, 214, 0.1)",
            "&:hover": {
              backgroundColor: "#3567D6",
              color: "#ffffff",
              transform: "translateY(-50%) scale(1.1)",
            },
            transition: "all 0.3s ease",
          }}
        >
          <ChevronLeft size={24} />
        </IconButton>

        {/* Right Arrow Button */}
        <IconButton
          onClick={() => scroll("right")}
          sx={{
            position: "absolute",
            right: { xs: 8, sm: 16, md: 24 },
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 2,
            backgroundColor: "#ffffff",
            color: "#3567D6",
            width: { xs: "40px", sm: "48px" },
            height: { xs: "40px", sm: "48px" },
            boxShadow: "0 4px 16px rgba(53, 103, 214, 0.2)",
            border: "1px solid rgba(53, 103, 214, 0.1)",
            "&:hover": {
              backgroundColor: "#3567D6",
              color: "#ffffff",
              transform: "translateY(-50%) scale(1.1)",
            },
            transition: "all 0.3s ease",
          }}
        >
          <ChevronRight size={24} />
        </IconButton>

        {/* Scrollable Container */}
        <Box
          ref={scrollContainerRef}
          sx={{
            overflowX: "auto",
            overflowY: "hidden",
            scrollbarWidth: "none", // Firefox
            msOverflowStyle: "none", // IE and Edge
            "&::-webkit-scrollbar": {
              display: "none", // Chrome, Safari, Opera
            },
            scrollBehavior: "smooth",
            px: { xs: 6, sm: 8, md: 10 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: { xs: 2.5, sm: 3 },
              pb: 2,
              minWidth: "max-content",
            }}
          >
            {testimonials.map((testimonial, index) => (
              <TestimonialCard key={index} testimonial={testimonial} index={index} />
            ))}
          </Box>
        </Box>
      </Box>

    </Box>
  );
};

export default TestimonialsSection;